import { t } from 'i18next';

import { LanguageCodeEditorEnum } from '@/models/CodeEditorActivity';

const INPUT_PATTERNS: Record<LanguageCodeEditorEnum, RegExp> = {
  [LanguageCodeEditorEnum.PYTHON]: /input\s*\(\s*\)/g,
  [LanguageCodeEditorEnum.JAVASCRIPT]: /prompt\s*\(\s*.*?\s*\)/g,
};

const PYTHON_CONVERSION_PATTERN =
  /\b(int|float|str|bool)\s*\(\s*input\s*\(\s*\)\s*\)/g;

function formatValue(
  value: string,
  language: LanguageCodeEditorEnum,
  conversionType?: string,
  formatForCode: boolean = true,
): string {
  const isNumeric = value.match(/^[0-9.]+$/);

  if (!formatForCode) {
    return value;
  }

  if (language === LanguageCodeEditorEnum.PYTHON) {
    if (conversionType === 'int' || conversionType === 'float') {
      return value;
    }
    return isNumeric ? value : `"${value}"`;
  }

  if (language === LanguageCodeEditorEnum.JAVASCRIPT) {
    return isNumeric ? value : `"${value}"`;
  }

  return value;
}

function getFallbackInputPattern(language: LanguageCodeEditorEnum): string {
  switch (language) {
    case LanguageCodeEditorEnum.PYTHON:
      return 'input()';
    case LanguageCodeEditorEnum.JAVASCRIPT:
      return 'prompt()';
    default:
      return '';
  }
}

function processPythonConversionPatterns(
  code: string,
  inputValues: string[],
  currentIndex: { value: number },
): string {
  return code.replace(PYTHON_CONVERSION_PATTERN, (match, conversionType) => {
    if (currentIndex.value < inputValues.length) {
      const value = inputValues[currentIndex.value++];
      return formatValue(
        value,
        LanguageCodeEditorEnum.PYTHON,
        conversionType,
        true,
      );
    } else {
      return match;
    }
  });
}

function processInputPatterns(
  code: string,
  inputValues: string[],
  currentIndex: { value: number },
  language: LanguageCodeEditorEnum,
): string {
  const inputPattern = INPUT_PATTERNS[language];

  if (!inputPattern) return code;

  return code.replace(inputPattern, () => {
    if (currentIndex.value < inputValues.length) {
      const value = inputValues[currentIndex.value++];
      return formatValue(value, language, undefined, true);
    } else {
      return getFallbackInputPattern(language);
    }
  });
}

function hasInputMethods(
  code: string,
  language: LanguageCodeEditorEnum,
): boolean {
  const inputPattern = INPUT_PATTERNS[language];

  if (!inputPattern) return false;

  if (language === LanguageCodeEditorEnum.PYTHON) {
    return inputPattern.test(code) || PYTHON_CONVERSION_PATTERN.test(code);
  }

  return inputPattern.test(code);
}

export function parseInputs(
  sourceCode: string,
  stdin: string | null,
  language: LanguageCodeEditorEnum,
): string {
  if (!stdin) {
    if (hasInputMethods(sourceCode, language)) {
      return sourceCode;
    } else {
      return t('codeValidation.notStdin');
    }
  }

  const inputValues = stdin.split(/\r?\n/).filter(val => val.trim() !== '');

  const formattedStdin = inputValues
    .map(value => formatValue(value, language, undefined, false))
    .join('\n');

  const currentIndex = { value: 0 };
  let resultCode = sourceCode;

  const hasInputs = hasInputMethods(sourceCode, language);

  if (hasInputs) {
    if (language === LanguageCodeEditorEnum.PYTHON) {
      resultCode = processPythonConversionPatterns(
        resultCode,
        inputValues,
        currentIndex,
      );
    }

    resultCode = processInputPatterns(
      resultCode,
      inputValues,
      currentIndex,
      language,
    );
    return resultCode;
  } else {
    return formattedStdin;
  }
}
